import isEmpty from 'lodash-es/isEmpty'
import omit from 'lodash-es/omit'
import pick from 'lodash-es/pick'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  ENTITY_LISTING_VIEW_TYPE,
} from '../../../constants/common'
import {
  SUBMENU_ALL_SECTION,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import {
  getConnectedRequestProfilePathname,
  getRequestProfilePathname,
} from '../../../utils/link'
import { getSubSearchParam } from '../../../utils/route'
import { FILTER_NAME_UNREAD } from '../../Filter'
import { REQUEST_PROFILE_TAB_EMAILS } from '../RequestProfile.constants'

import {
  REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_BY_CONTACT_TYPE,
  REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY,
  REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL,
  REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_BY_AUDIENCE,
  REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ORDER,
  REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_TITLE_KEY,
  REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE,
  REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_SUB_MENU,
  REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL,
  REQUEST_PROFILE_TAB_EMAILS_FOLDER_ORDER,
  REQUEST_PROFILE_TAB_EMAILS_FOLDER_TITLE_KEY,
  REQUEST_PROFILE_TAB_EMAILS_MASS_ACTION,
  REQUEST_PROFILE_TAB_EMAILS_PERMANENT_FILTERS,
  REQUEST_PROFILE_TAB_EMAILS_PREVIEW_VIEW_MIN_SCREEN_WIDTH,
  REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY,
  REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY,
  REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY,
} from './RequestProfileTabEmails.constants'

export function isRequestProfileTabEmailsMassActionDelete(action) {
  return action === REQUEST_PROFILE_TAB_EMAILS_MASS_ACTION.Delete
}

export function getRequestProfileTabEmailsContactTypeOptions({ t }) {
  return REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ORDER.map(value => ({
    label: t(REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_TITLE_KEY[value]),
    value,
  }))
}

export function getRequestProfileTabEmailsFolderOptions({
  foldersUnreadCount,
  t,
}) {
  return REQUEST_PROFILE_TAB_EMAILS_FOLDER_ORDER.map(value => ({
    label: t(REQUEST_PROFILE_TAB_EMAILS_FOLDER_TITLE_KEY[value]),
    unreadCount: foldersUnreadCount?.[value],
    value,
  }))
}

export function getRequestProfileTabEmailsSelectedOption({
  activeSubTab,
  options,
  subTabKey,
}) {
  return options.find(option => option.value === activeSubTab?.[subTabKey])
}

export function getRequestProfileTabEmailsActiveSubTabFromFilters(
  filterValues
) {
  const audience =
    filterValues?.[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE]
  const subMenu = filterValues?.[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_SUB_MENU]

  return {
    [REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY]:
      REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_BY_AUDIENCE[audience] ??
      REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL,
    [REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY]:
      subMenu || REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL,
  }
}

export function getRequestProfileTabEmailsFiltersWithSubTab({
  contactType,
  filterValues,
  subMenu,
}) {
  const audience =
    REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_BY_CONTACT_TYPE[contactType]

  const next = {
    ...(filterValues || EMPTY_OBJECT),
    [REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_SUB_MENU]: subMenu,
  }

  if (audience) {
    next[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE] = audience
  } else {
    delete next[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE]
  }

  return next
}

export function getRequestProfileTabEmailsPermanentFilters({ filterValues }) {
  return pick(filterValues, REQUEST_PROFILE_TAB_EMAILS_PERMANENT_FILTERS)
}

export function parseRequestProfileTabEmailsPermanentFilters(filters) {
  if (!filters) {
    return EMPTY_LIST
  }

  return REQUEST_PROFILE_TAB_EMAILS_PERMANENT_FILTERS.reduce(
    (acc, key) => (filters[key] ? acc.concat([[key, filters[key]]]) : acc),
    []
  )
}

export function getRequestProfileTabEmailsThreadPageLink({
  availableSections,
  isStaff,
  mailUuid,
  outboundUuid,
  requestId,
}) {
  // The link deep-links into the Emails tab, which non-staff users don't have.
  if (!isStaff) {
    return null
  }

  const threadParam = `${REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY}=${mailUuid}`

  // Connected (iDWELL-to-iDWELL) requests must open via the id2id profile
  // route (landing on the Emails tab so the thread modal auto-opens).
  if (outboundUuid) {
    return getConnectedRequestProfilePathname({
      additionalParams: `/${REQUEST_PROFILE_TAB_EMAILS}?${threadParam}&${SUBMENU_PARAM_KEY}=${SUBMENU_ALL_SECTION}`,
      availableSections,
      id: requestId,
      outboundUuid,
    })
  }

  return getRequestProfilePathname({
    additionalParams: `?${threadParam}`,
    availableSections,
    id: requestId,
  })
}

export function getRequestProfileTabEmailsSubMenu(filterValues) {
  return (
    filterValues?.[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_SUB_MENU] ||
    REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL
  )
}

export function isRequestProfileTabEmailsAllFolder(subMenu) {
  return subMenu === REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL
}

export function hasRequestProfileTabEmailsActiveFilters(filterValues) {
  return (
    !isRequestProfileTabEmailsAllFolder(
      getRequestProfileTabEmailsSubMenu(filterValues)
    ) ||
    filterValues?.[REQUEST_PROFILE_TAB_EMAILS_FILTER_KEY_AUDIENCE] != null ||
    !isEmpty(omit(filterValues, REQUEST_PROFILE_TAB_EMAILS_PERMANENT_FILTERS))
  )
}

export function shouldResetRequestProfileTabEmailsFiltersOnSearch({
  filterValues,
  searchQuery,
}) {
  return (
    searchQuery.length > 0 &&
    hasRequestProfileTabEmailsActiveFilters(filterValues)
  )
}

export function getRequestProfileTabEmailsDefaultFilters() {
  return getRequestProfileTabEmailsFiltersWithSubTab({
    contactType: REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL,
    filterValues: EMPTY_OBJECT,
    subMenu: REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL,
  })
}

// Reads the sub-tab dimensions from the URL query (folder via the shared `sub`
// key, contact type via `audience`), returning only valid values (else null).
export function getRequestProfileTabEmailsSubTabFromSearchParams(searchParams) {
  const folder = getSubSearchParam(searchParams)
  const contactType = searchParams.get(
    REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY
  )

  return {
    [REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY]:
      REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ORDER.includes(contactType)
        ? contactType
        : null,
    [REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY]:
      REQUEST_PROFILE_TAB_EMAILS_FOLDER_ORDER.includes(folder) ? folder : null,
  }
}

export function isRequestProfileTabEmailsAllContactType(contactType) {
  return contactType === REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL
}

export function getIsEmailPreviewAvailable({ isPreview, screenWidth }) {
  return (
    !isPreview &&
    screenWidth > REQUEST_PROFILE_TAB_EMAILS_PREVIEW_VIEW_MIN_SCREEN_WIDTH
  )
}

export function getIsRequestProfileTabEmailsListEmpty({ isLoading, items }) {
  const emails = items?.items ?? EMPTY_LIST

  return !isLoading && emails.length === 0
}

export function isRequestProfileTabEmailsUnreadFilterActive(filterValues) {
  return filterValues?.[FILTER_NAME_UNREAD] === true
}

export function getRequestProfileTabEmailsListViewType({
  activeItem,
  hasError,
  isEmptyList,
  selectedListViewType,
}) {
  return (hasError || isEmptyList) && activeItem == null
    ? ENTITY_LISTING_VIEW_TYPE.table
    : selectedListViewType
}

// Builds the search params for a given sub-tab: the folder goes into the `sub`
// param, and the audience param is set for a specific contact type or removed
// for the "all" default.
export function getRequestProfileTabEmailsSearchParamsWithSubTab(
  searchParams,
  { contactType, folder }
) {
  const next = new URLSearchParams(searchParams)

  next.set(SUBMENU_PARAM_KEY, folder)

  if (!contactType || isRequestProfileTabEmailsAllContactType(contactType)) {
    next.delete(REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY)
  } else {
    next.set(REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY, contactType)
  }

  return next
}

// Seeds the freshly-loaded filter state with the active sub-tab so the FIRST
// list fetch carries an explicit `sub_menu` (defaulting to "all") instead of
// relying on the backend's implicit default. The URL sub-tab wins when present
// (so a deep link is reflected), otherwise the value persisted in the filter
// state is kept. Seeding here does not itself write to the server, though the
// value becomes part of the local filter state and so may be persisted later by
// a subsequent save action (e.g. an ordering or page-size change).
export function getRequestProfileTabEmailsInitialFilters({
  filters,
  searchParams,
}) {
  const urlSubTab =
    getRequestProfileTabEmailsSubTabFromSearchParams(searchParams)
  const contactType =
    urlSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY]
  const folder = urlSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY]

  const current = getRequestProfileTabEmailsActiveSubTabFromFilters(filters)

  return getRequestProfileTabEmailsFiltersWithSubTab({
    contactType:
      contactType ??
      current[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY],
    filterValues: filters,
    subMenu: folder ?? current[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY],
  })
}
